import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, Save, X, RefreshCw, Clock, Calendar, Ban, Check } from 'lucide-react';
import kitchenApi from '../services/kitchenApiService';

const DAY_NAMES = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const DAY_SHORT = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function timeOptions(step = 30) {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      opts.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
    }
  }
  return opts;
}
const TIME_OPTS = timeOptions(30);

function generateSlots(start, end, interval) {
  if (!start || !end || !interval) return [];
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let cur = sh * 60 + sm;
  const fin = eh * 60 + em;
  const slots = [];
  while (cur < fin) {
    slots.push(`${String(Math.floor(cur/60)).padStart(2,'0')}:${String(cur%60).padStart(2,'0')}`);
    cur += interval;
  }
  return slots;
}

function fmt12(t) {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function SectionCard({ title, subtitle, action, children }) {
  return (
    <div className="k-card" style={{ marginBottom: 20 }}>
      <div className="k-card-header">
        <div>
          <div className="k-card-title">{title}</div>
          {subtitle && <div className="k-card-sub">{subtitle}</div>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function SchedulePage() {
  const [tab,      setTab]      = useState('hours');
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState('');
  const [toastErr, setToastErr] = useState('');
  const [blocked,  setBlocked]  = useState([]);

  const [hoursForm, setHoursForm] = useState({
    operating_days: [0,1,2,3,4,5,6],
    start_time: '09:00', end_time: '21:00',
    last_order_time: '', is_kitchen_open: true,
  });

  const [slotMode,      setSlotMode]      = useState('auto');
  const [slotInterval,  setSlotInterval]  = useState(30);
  const [explicitSlots, setExplicitSlots] = useState([]);
  const [newSlotTime,   setNewSlotTime]   = useState('09:00');
  const [previewSlots,  setPreviewSlots]  = useState([]);

  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockForm,     setBlockForm]     = useState({ start_datetime: '', end_datetime: '', reason: '' });
  const [blockSaving,   setBlockSaving]   = useState(false);
  const [editBlockId,   setEditBlockId]   = useState(null);

  const toast_ = (msg, err = false) => {
    if (err) { setToastErr(msg); setTimeout(() => setToastErr(''), 3500); }
    else     { setToast(msg);    setTimeout(() => setToast(''), 3000); }
  };

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const [cfgR, slotsR, blockR] = await Promise.all([
      kitchenApi.getKitchenConfig(),
      kitchenApi.getKitchenTimeSlots(),
      kitchenApi.getBlockedPeriods(),
    ]);
    if (cfgR.success && cfgR.data) {
      const d = cfgR.data;
      setHoursForm({
        operating_days:  d.operating_days  ?? [0,1,2,3,4,5,6],
        start_time:      d.start_time      || '09:00',
        end_time:        d.end_time        || '21:00',
        last_order_time: d.last_order_time || '',
        is_kitchen_open: d.is_kitchen_open ?? true,
      });
    }
    if (slotsR.success && slotsR.data) {
      const sd = slotsR.data;
      const explicit = sd.explicit_time_slots || [];
      const interval = sd.slot_interval || 30;
      const start    = sd.start_time || cfgR.data?.start_time || '09:00';
      const end      = sd.end_time   || cfgR.data?.end_time   || '21:00';
      setSlotInterval(interval);
      if (explicit.length > 0) {
        setSlotMode('explicit'); setExplicitSlots(explicit);
      } else {
        setSlotMode('auto'); setExplicitSlots([]);
        setPreviewSlots(generateSlots(start, end, interval));
      }
    }
    if (blockR.success) {
      const bd = blockR.data;
      setBlocked(Array.isArray(bd) ? bd : (bd?.blocked_periods || []));
    }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (slotMode === 'auto') {
      setPreviewSlots(generateSlots(hoursForm.start_time, hoursForm.end_time, slotInterval));
    }
  }, [slotMode, hoursForm.start_time, hoursForm.end_time, slotInterval]);

  const saveHours = async () => {
    setSaving(true);
    const res = await kitchenApi.updateKitchenConfig({
      operating_days: hoursForm.operating_days,
      start_time:     hoursForm.start_time,
      end_time:       hoursForm.end_time,
      last_order_time: hoursForm.last_order_time || null,
      is_kitchen_open: hoursForm.is_kitchen_open,
    });
    setSaving(false);
    if (res.success) { toast_('Operating hours saved!'); loadAll(true); }
    else toast_(res.error || 'Failed to save', true);
  };

  const saveSlots = async () => {
    setSaving(true);
    const res = await kitchenApi.updateKitchenConfig({
      explicit_time_slots: slotMode === 'explicit' ? explicitSlots : [],
      slot_interval: slotInterval,
      ...(slotMode === 'auto' ? { start_time: hoursForm.start_time, end_time: hoursForm.end_time } : {}),
    });
    setSaving(false);
    if (res.success) { toast_('Time slots saved!'); loadAll(true); }
    else toast_(res.error || 'Failed to save', true);
  };

  const addExplicitSlot = () => {
    if (!newSlotTime) return;
    if (explicitSlots.includes(newSlotTime)) { toast_('Slot already exists', true); return; }
    setExplicitSlots(prev => [...prev, newSlotTime].sort());
  };

  const toggleOpen = async () => {
    const next = !hoursForm.is_kitchen_open;
    setHoursForm(f => ({ ...f, is_kitchen_open: next }));
    const res = await kitchenApi.toggleKitchenOpen(next);
    if (!res.success) { setHoursForm(f => ({ ...f, is_kitchen_open: !next })); toast_(res.error || 'Toggle failed', true); }
    else toast_(next ? 'Kitchen is now OPEN' : 'Kitchen is now CLOSED');
  };

  const toggleDay = (d) => setHoursForm(f => {
    const days = f.operating_days.includes(d)
      ? f.operating_days.filter(x => x !== d)
      : [...f.operating_days, d].sort((a,b) => a-b);
    return { ...f, operating_days: days };
  });

  const saveBlock = async () => {
    if (!blockForm.start_datetime || !blockForm.end_datetime) return;
    setBlockSaving(true);
    const res = editBlockId
      ? await kitchenApi.updateBlockedPeriod(editBlockId, blockForm)
      : await kitchenApi.createBlockedPeriod(blockForm);
    setBlockSaving(false);
    if (res.success) {
      setShowBlockForm(false); setEditBlockId(null);
      setBlockForm({ start_datetime: '', end_datetime: '', reason: '' });
      loadAll(true); toast_(editBlockId ? 'Period updated!' : 'Period created!');
    } else toast_(res.error || 'Failed to save', true);
  };

  const editBlock = (b) => {
    const fmt = s => s ? s.replace(' ','T').slice(0,16) : '';
    setEditBlockId(b.blocked_period_id || b.id);
    setBlockForm({ start_datetime: fmt(b.start_datetime), end_datetime: fmt(b.end_datetime), reason: b.reason || '' });
    setShowBlockForm(true);
  };

  const deleteBlock = async (id) => {
    if (!window.confirm('Remove this blocked period?')) return;
    await kitchenApi.deleteBlockedPeriod(id);
    loadAll(true); toast_('Blocked period removed');
  };

  if (loading) return <div className="k-loading-center"><div className="k-spinner" /></div>;

  const isOpen = hoursForm.is_kitchen_open;
  const displaySlots = slotMode === 'explicit' ? explicitSlots : previewSlots;

  return (
    <div>
      {/* Header */}
      <div className="k-page-header">
        <div>
          <h1 className="k-page-title">Schedule & Availability</h1>
          <p className="k-page-sub">Control when your kitchen accepts orders</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={toggleOpen} style={{
            padding:'8px 18px', borderRadius:8, border:'none', fontWeight:700, fontSize:13,
            cursor:'pointer', background: isOpen ? '#22c55e' : '#ef4444', color:'#fff',
            display:'flex', alignItems:'center', gap:6, transition:'background .2s',
          }}>
            <span style={{ width:8,height:8,borderRadius:'50%',background:'rgba(255,255,255,.7)',display:'inline-block' }} />
            {isOpen ? 'Kitchen Open' : 'Kitchen Closed'}
          </button>
          <button className="k-btn-icon" onClick={() => loadAll()} title="Refresh"><RefreshCw size={16} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="k-tabs" style={{ marginBottom:24, maxWidth:520 }}>
        <button className={`k-tab${tab==='hours'   ? ' active':''}`} onClick={() => setTab('hours')}><Calendar size={14} /> Opening Hours</button>
        <button className={`k-tab${tab==='slots'   ? ' active':''}`} onClick={() => setTab('slots')}><Clock size={14} /> Time Slots</button>
        <button className={`k-tab${tab==='blocked' ? ' active':''}`} onClick={() => setTab('blocked')}><Ban size={14} /> Blocked Periods</button>
      </div>

      {/* ── OPENING HOURS TAB ── */}
      {tab === 'hours' && (
        <div>
          <SectionCard title="Operating Days" subtitle="Select which days your kitchen accepts orders">
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginTop:4 }}>
              {DAY_NAMES.map((day, idx) => {
                const on = hoursForm.operating_days.includes(idx);
                return (
                  <button key={day} onClick={() => toggleDay(idx)} style={{
                    padding:'8px 16px', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer',
                    border: `2px solid ${on ? 'var(--k-primary)' : 'var(--k-border)'}`,
                    background: on ? 'var(--k-primary)' : 'var(--k-bg)',
                    color: on ? '#fff' : 'var(--k-text)',
                    display:'flex', alignItems:'center', gap:4, transition:'all .15s',
                  }}>
                    {on && <Check size={11} />}
                    {DAY_SHORT[idx]}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop:10, fontSize:12, color:'var(--k-text-sub)' }}>
              {hoursForm.operating_days.length === 0
                ? '⚠️ No days selected — kitchen will not accept orders'
                : `${hoursForm.operating_days.length} day${hoursForm.operating_days.length>1?'s':''}: ${hoursForm.operating_days.map(d=>DAY_SHORT[d]).join(', ')}`}
            </div>
          </SectionCard>

          <SectionCard title="Daily Operating Hours" subtitle="Set when the kitchen opens and closes">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:20, maxWidth:560 }}>
              {[
                { key:'start_time', label:'Opens At', req: true },
                { key:'end_time',   label:'Closes At', req: true },
                { key:'last_order_time', label:'Last Order Time', req: false },
              ].map(({ key, label, req }) => (
                <div key={key} className="k-form-group" style={{ margin:0 }}>
                  <label className={`k-label${req?' required':''}`}>{label}</label>
                  <select className="k-select"
                    value={hoursForm[key]}
                    onChange={e => setHoursForm(f => ({ ...f, [key]: e.target.value }))}
                  >
                    {!req && <option value="">Same as close</option>}
                    {TIME_OPTS.map(t => <option key={t} value={t}>{fmt12(t)}</option>)}
                  </select>
                  {key === 'last_order_time' && <div style={{ fontSize:11, color:'var(--k-text-sub)', marginTop:3 }}>Cut-off for new orders</div>}
                </div>
              ))}
            </div>

            {/* Visual bar */}
            <div style={{ marginTop:20, padding:'14px 16px', background:'var(--k-bg-secondary,#f8f9fa)', borderRadius:10, display:'flex', alignItems:'center', gap:12, fontSize:13 }}>
              <span style={{ fontWeight:700, color:'var(--k-primary)' }}>{fmt12(hoursForm.start_time)}</span>
              <div style={{ flex:1, height:6, borderRadius:4, background:'linear-gradient(90deg,#22c55e,var(--k-primary))', position:'relative' }}>
                {hoursForm.last_order_time && (
                  <div title={`Last order: ${fmt12(hoursForm.last_order_time)}`} style={{
                    position:'absolute', right:'15%', top:'50%', transform:'translateY(-50%)',
                    width:12, height:12, borderRadius:'50%', background:'#f59e0b', border:'2px solid #fff',
                  }} />
                )}
              </div>
              <span style={{ fontWeight:700, color:'var(--k-danger)' }}>{fmt12(hoursForm.end_time)}</span>
            </div>
            {hoursForm.last_order_time && (
              <div style={{ fontSize:12, color:'var(--k-text-sub)', marginTop:5 }}>
                🟡 Last order accepted at {fmt12(hoursForm.last_order_time)}
              </div>
            )}
          </SectionCard>

          {/* 7-day preview */}
          <SectionCard title="Next 7 Days Preview" subtitle="Upcoming availability based on settings">
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {Array.from({ length:7 }, (_, i) => {
                const d = new Date(); d.setDate(d.getDate() + i);
                const dow = (d.getDay() + 6) % 7;
                const on  = hoursForm.operating_days.includes(dow);
                const label = i===0?'Today':i===1?'Tomorrow':DAY_SHORT[dow];
                const dateStr = d.toLocaleDateString('en-US', { month:'short', day:'numeric' });
                return (
                  <div key={i} style={{
                    padding:'10px 14px', borderRadius:10, textAlign:'center', minWidth:72,
                    border:`2px solid ${on ? 'var(--k-primary)' : 'var(--k-border)'}`,
                    background: on ? 'var(--k-primary-light,#fff7f0)' : 'var(--k-bg)',
                    opacity: on ? 1 : .5,
                  }}>
                    <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', color:'var(--k-text-sub)' }}>{label}</div>
                    <div style={{ fontSize:12, fontWeight:600, margin:'2px 0' }}>{dateStr}</div>
                    {on ? (
                      <>
                        <div style={{ fontSize:11, color:'var(--k-primary)', fontWeight:700 }}>✓ Open</div>
                        <div style={{ fontSize:10, color:'var(--k-text-sub)' }}>{fmt12(hoursForm.start_time)}–{fmt12(hoursForm.end_time)}</div>
                      </>
                    ) : <div style={{ fontSize:11, color:'var(--k-text-sub)' }}>Closed</div>}
                  </div>
                );
              })}
            </div>
          </SectionCard>

          <button className="k-btn k-btn-primary" onClick={saveHours} disabled={saving}>
            {saving ? <span className="k-spinner k-spinner-sm" style={{ borderTopColor:'#fff' }} /> : <><Save size={14} /> Save Operating Hours</>}
          </button>
        </div>
      )}

      {/* ── TIME SLOTS TAB ── */}
      {tab === 'slots' && (
        <div>
          {/* Mode picker */}
          <SectionCard title="Slot Generation Mode" subtitle="Choose how order time slots are created">
            <div style={{ display:'flex', gap:12, marginTop:4 }}>
              {[
                { v:'auto',     label:'⚡ Auto-Generate', desc:'Slots every N minutes during opening hours' },
                { v:'explicit', label:'✏️ Custom Slots',  desc:'Manually pick exactly which times are available' },
              ].map(({ v, label, desc }) => (
                <button key={v} onClick={() => setSlotMode(v)} style={{
                  flex:1, padding:'14px 16px', borderRadius:10, textAlign:'left', cursor:'pointer',
                  border:`2px solid ${slotMode===v ? 'var(--k-primary)' : 'var(--k-border)'}`,
                  background: slotMode===v ? 'var(--k-primary-light,#fff7f0)' : 'var(--k-bg)',
                }}>
                  <div style={{ fontWeight:700, fontSize:14 }}>{label}</div>
                  <div style={{ fontSize:12, color:'var(--k-text-sub)', marginTop:4 }}>{desc}</div>
                </button>
              ))}
            </div>
          </SectionCard>

          {/* Auto settings */}
          {slotMode === 'auto' && (
            <SectionCard title="Auto-Generation Settings" subtitle="Slots created every N minutes during opening hours">
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:20, maxWidth:500 }}>
                <div className="k-form-group" style={{ margin:0 }}>
                  <label className="k-label">Interval (minutes)</label>
                  <select className="k-select" value={slotInterval} onChange={e => setSlotInterval(Number(e.target.value))}>
                    {[15,20,30,45,60,90,120].map(v => <option key={v} value={v}>{v} min</option>)}
                  </select>
                </div>
                <div className="k-form-group" style={{ margin:0 }}>
                  <label className="k-label">From</label>
                  <select className="k-select" value={hoursForm.start_time} onChange={e => setHoursForm(f => ({ ...f, start_time: e.target.value }))}>
                    {TIME_OPTS.map(t => <option key={t} value={t}>{fmt12(t)}</option>)}
                  </select>
                </div>
                <div className="k-form-group" style={{ margin:0 }}>
                  <label className="k-label">Until</label>
                  <select className="k-select" value={hoursForm.end_time} onChange={e => setHoursForm(f => ({ ...f, end_time: e.target.value }))}>
                    {TIME_OPTS.map(t => <option key={t} value={t}>{fmt12(t)}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginTop:14, display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ fontSize:13, color:'var(--k-text-sub)' }}>{previewSlots.length} slots will be generated</span>
                <button className="k-btn k-btn-sm k-btn-outline" style={{ fontSize:12 }} onClick={() => {
                  setExplicitSlots(generateSlots(hoursForm.start_time, hoursForm.end_time, slotInterval));
                  setSlotMode('explicit');
                }}>
                  Convert to Custom →
                </button>
              </div>
            </SectionCard>
          )}

          {/* Explicit / edit slots */}
          {slotMode === 'explicit' && (
            <SectionCard
              title="Custom Time Slots"
              subtitle="Add or remove individual time slots"
              action={
                <button className="k-btn k-btn-sm k-btn-outline" style={{ fontSize:12 }} onClick={() => {
                  setExplicitSlots(generateSlots(hoursForm.start_time, hoursForm.end_time, slotInterval));
                }}>
                  <RefreshCw size={12} /> Re-generate
                </button>
              }
            >
              <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'flex-end' }}>
                <div className="k-form-group" style={{ margin:0 }}>
                  <label className="k-label">Add Slot</label>
                  <select className="k-select" style={{ width:140 }} value={newSlotTime} onChange={e => setNewSlotTime(e.target.value)}>
                    {TIME_OPTS.map(t => <option key={t} value={t}>{fmt12(t)}</option>)}
                  </select>
                </div>
                <button className="k-btn k-btn-primary k-btn-sm" onClick={addExplicitSlot}><Plus size={13} /> Add</button>
              </div>

              {explicitSlots.length === 0 ? (
                <div className="k-empty" style={{ padding:'24px 0' }}>
                  <div className="k-empty-icon">🕐</div>
                  <div className="k-empty-title">No slots added yet</div>
                  <div className="k-empty-sub">Add times above, or click "Re-generate" to populate from opening hours</div>
                </div>
              ) : (
                <>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {explicitSlots.map(t => (
                      <div key={t} style={{
                        display:'flex', alignItems:'center', gap:6,
                        padding:'6px 12px', borderRadius:20,
                        background:'var(--k-primary-light,#fff7f0)',
                        border:'1px solid var(--k-primary)',
                        fontSize:13, fontWeight:600, color:'var(--k-primary)',
                      }}>
                        <Clock size={12} /> {fmt12(t)}
                        <button onClick={() => setExplicitSlots(prev => prev.filter(s => s !== t))} style={{
                          background:'none', border:'none', cursor:'pointer', padding:0, lineHeight:1, color:'var(--k-primary)', display:'flex',
                        }}>
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop:10, fontSize:12, color:'var(--k-text-sub)' }}>
                    {explicitSlots.length} slot{explicitSlots.length!==1?'s':''} · first: {fmt12(explicitSlots[0])} · last: {fmt12(explicitSlots[explicitSlots.length-1])}
                  </div>
                </>
              )}
            </SectionCard>
          )}

          {/* Preview */}
          <SectionCard
            title={slotMode==='auto' ? 'Preview — Auto-Generated Slots' : 'Active Custom Slots'}
            subtitle="Times customers can choose when placing orders"
          >
            {displaySlots.length === 0 ? (
              <div className="k-empty" style={{ padding:'20px 0' }}>
                <div className="k-empty-icon">⏰</div>
                <div className="k-empty-title">No slots to preview</div>
              </div>
            ) : (
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {displaySlots.map(t => (
                  <div key={t} style={{
                    padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:600,
                    background:'var(--k-bg-secondary,#f1f5f9)', border:'1px solid var(--k-border)',
                  }}>
                    {fmt12(t)}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <button className="k-btn k-btn-primary" onClick={saveSlots} disabled={saving}>
            {saving ? <span className="k-spinner k-spinner-sm" style={{ borderTopColor:'#fff' }} /> : <><Save size={14} /> Save Time Slots</>}
          </button>
        </div>
      )}

      {/* ── BLOCKED PERIODS TAB ── */}
      {tab === 'blocked' && (
        <div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
            <button className="k-btn k-btn-primary" onClick={() => {
              setEditBlockId(null);
              setBlockForm({ start_datetime:'', end_datetime:'', reason:'' });
              setShowBlockForm(true);
            }}>
              <Plus size={14} /> Block Period
            </button>
          </div>
          <div className="k-card">
            <div className="k-card-header">
              <div>
                <div className="k-card-title">Blocked Order Periods</div>
                <div className="k-card-sub">During these windows, no orders will be accepted</div>
              </div>
            </div>
            {blocked.length === 0 ? (
              <div className="k-empty">
                <div className="k-empty-icon">📅</div>
                <div className="k-empty-title">No blocked periods</div>
                <div className="k-empty-sub">Block periods for holidays, maintenance, or anything else</div>
              </div>
            ) : (
              <div className="k-table-wrap">
                <table className="k-table">
                  <thead><tr><th>Start</th><th>End</th><th>Duration</th><th>Reason</th><th></th></tr></thead>
                  <tbody>
                    {blocked.map(b => {
                      const id    = b.blocked_period_id || b.id;
                      const start = b.start_datetime ? new Date(b.start_datetime) : null;
                      const end   = b.end_datetime   ? new Date(b.end_datetime)   : null;
                      const diffH = start && end ? Math.round((end - start) / 360000) / 10 : null;
                      const isPast = end && end < new Date();
                      const fmtDt = d => d
                        ? d.toLocaleDateString('en-US',{month:'short',day:'numeric'}) + ' ' + fmt12(d.toTimeString().slice(0,5))
                        : '—';
                      return (
                        <tr key={id} style={{ opacity: isPast ? .55 : 1 }}>
                          <td style={{ fontWeight:600 }}>{fmtDt(start)}</td>
                          <td>{fmtDt(end)}</td>
                          <td style={{ color:'var(--k-text-sub)', fontSize:12 }}>
                            {diffH != null ? `${diffH}h` : '—'}
                            {isPast && <span style={{ marginLeft:6, fontSize:10, fontWeight:700, color:'var(--k-text-sub)' }}>PAST</span>}
                          </td>
                          <td style={{ color:'var(--k-text-sub)' }}>{b.reason || '—'}</td>
                          <td>
                            <div style={{ display:'flex', gap:6 }}>
                              <button className="k-btn-icon" style={{ padding:6 }} onClick={() => editBlock(b)} title="Edit"><Edit2 size={13} /></button>
                              <button className="k-btn-icon" style={{ color:'var(--k-danger)', padding:6 }} onClick={() => deleteBlock(id)} title="Delete"><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Block Period Modal */}
      {showBlockForm && (
        <div className="k-modal-overlay">
          <div className="k-modal k-modal-sm">
            <div className="k-modal-header">
              <div className="k-modal-title">{editBlockId ? 'Edit Blocked Period' : 'Block Time Period'}</div>
              <button className="k-btn-icon" onClick={() => { setShowBlockForm(false); setEditBlockId(null); }}><X size={18} /></button>
            </div>
            <div className="k-modal-body">
              <div className="k-form-group">
                <label className="k-label required">Start Date & Time</label>
                <input className="k-input" type="datetime-local" value={blockForm.start_datetime}
                  onChange={e => setBlockForm(f => ({ ...f, start_datetime: e.target.value }))} />
              </div>
              <div className="k-form-group">
                <label className="k-label required">End Date & Time</label>
                <input className="k-input" type="datetime-local" value={blockForm.end_datetime}
                  onChange={e => setBlockForm(f => ({ ...f, end_datetime: e.target.value }))} />
              </div>
              <div className="k-form-group">
                <label className="k-label">Reason</label>
                <input className="k-input" placeholder="e.g. Holiday, Maintenance, Staff Training"
                  value={blockForm.reason} onChange={e => setBlockForm(f => ({ ...f, reason: e.target.value }))} />
              </div>
              {/* Quick presets */}
              <div style={{ marginTop:4 }}>
                <div style={{ fontSize:11, color:'var(--k-text-sub)', marginBottom:6, fontWeight:700, letterSpacing:.5, textTransform:'uppercase' }}>Quick Presets</div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {[
                    { l:'Today',    fn: () => { const s=new Date(); s.setHours(0,0,0,0); return [s, new Date(s.getTime()+86400000)]; }},
                    { l:'Tomorrow', fn: () => { const s=new Date(); s.setDate(s.getDate()+1); s.setHours(0,0,0,0); return [s, new Date(s.getTime()+86400000)]; }},
                    { l:'3 Days',   fn: () => { const s=new Date(); s.setHours(0,0,0,0); return [s, new Date(s.getTime()+3*86400000)]; }},
                    { l:'1 Week',   fn: () => { const s=new Date(); s.setHours(0,0,0,0); return [s, new Date(s.getTime()+7*86400000)]; }},
                  ].map(({ l, fn }) => (
                    <button key={l} className="k-btn k-btn-sm k-btn-ghost" style={{ fontSize:11 }} onClick={() => {
                      const [s,e] = fn();
                      const toL = d => new Date(d.getTime()-d.getTimezoneOffset()*60000).toISOString().slice(0,16);
                      setBlockForm(f => ({ ...f, start_datetime: toL(s), end_datetime: toL(e) }));
                    }}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="k-modal-footer">
              <button className="k-btn k-btn-ghost" onClick={() => { setShowBlockForm(false); setEditBlockId(null); }}>Cancel</button>
              <button className="k-btn k-btn-primary" onClick={saveBlock}
                disabled={blockSaving || !blockForm.start_datetime || !blockForm.end_datetime}>
                {blockSaving
                  ? <span className="k-spinner k-spinner-sm" style={{ borderTopColor:'#fff' }} />
                  : editBlockId ? 'Update Period' : 'Block Period'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      {toast && <div className="k-toast-container"><div className="k-toast success">✓ {toast}</div></div>}
      {toastErr && <div className="k-toast-container"><div className="k-toast error">✗ {toastErr}</div></div>}
    </div>
  );
}
